import { useEffect, useMemo, useState, type Key } from 'react';
import {
  Card,
  Descriptions,
  Table,
  Button,
  Typography,
  Space,
  Empty,
  Spin,
  Tag,
  Tooltip,
  Tabs,
  Popconfirm,
  Input,
  App,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined,
  MailOutlined,
} from '@ant-design/icons';
import type { BomRow } from '@bom/shared';
import { useSessionStore } from '../../stores/sessionStore';
import { useBomsV2, useBomRows, useDeleteBomV2, useResendBomNotify } from '../../api/boms-v2';
import { useAffectedParts } from '../../api/ecns';
import { NoTenant } from '../../components/NoTenant';
import { fmtTime } from '../../lib/format';
import { exportBomRowsToXlsx } from '../../lib/excel';
import { openAttachment } from '../../api/attachments';
import { ApiError } from '../../lib/apiClient';
import { BomUploadModal } from './BomUploadModal';
import { SendMailModal } from '../../components/SendMailModal';
import { PartTimelineDrawer } from './PartTimelineDrawer';
import { EcnListPage } from './EcnListPage';
import { SpecListPage } from './SpecListPage';

type TreeRow = BomRow & { key: string; children?: TreeRow[] };

/** 「层级」列(0 / .1 / ..2)的深度 = 前导点数；"0" 视为 0。 */
function depthOf(label?: string): number {
  if (!label || label === '0') return 0;
  const m = label.match(/^\.+/);
  return m ? m[0].length : 0;
}

/**
 * 由扁平行(按 seq 先序)重建 BOM 树：栈式——弹出栈顶直到其深度 < 当前，则栈顶即父。
 * 用「层级」深度而非母件编号，避免同一料号被多处引用时的歧义；能容忍跳层。
 */
function buildBomTree(rows: BomRow[]): { tree: TreeRow[]; parentKeys: string[] } {
  const tree: TreeRow[] = [];
  const stack: { node: TreeRow; depth: number }[] = [];
  const parentKeys = new Set<string>();
  rows.forEach((r, i) => {
    const node: TreeRow = { ...r, key: `r${i}` };
    const d = depthOf(r.level_label);
    while (stack.length && stack[stack.length - 1]!.depth >= d) stack.pop();
    const top = stack[stack.length - 1];
    if (top) {
      (top.node.children ??= []).push(node);
      parentKeys.add(top.node.key);
    } else {
      tree.push(node);
    }
    stack.push({ node, depth: d });
  });
  return { tree, parentKeys: [...parentKeys] };
}

/** 读 extra 里的原始列（母件编号 / 归属BOM / 子BOMID 等未映射列都在 extra） */
function ex(r: BomRow, key: string): string {
  const v = (r.extra as Record<string, unknown> | undefined)?.[key];
  return typeof v === 'string' && v ? v : '';
}

/** YYYYMMDD → YYYY-MM-DD */
function fmtYmd(d?: string): string {
  if (!d) return '';
  return d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : d;
}

export function BomDetailPage() {
  const { message } = App.useApp();
  const tid = useSessionStore((s) => s.currentTenant?.id);
  const role = useSessionStore((s) => s.role);
  const isPlatformAdmin = useSessionStore((s) => s.user?.is_platform_admin ?? false);
  const canManage = isPlatformAdmin || role === 'admin';

  const boms = useBomsV2(tid);
  const del = useDeleteBomV2(tid ?? '');
  const resend = useResendBomNotify(tid ?? '');
  const affected = useAffectedParts(tid);
  // 一个产品只有一份 BOM：取最新的那份
  const m = useMemo(() => {
    const list = boms.data ?? [];
    return [...list].sort((a, b) => (b.uploaded_at ?? 0) - (a.uploaded_at ?? 0))[0];
  }, [boms.data]);
  const rows = useBomRows(tid, m?.id);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [timelinePart, setTimelinePart] = useState<string | undefined>();
  const [kw, setKw] = useState('');

  // 搜索时：过滤成扁平匹配列表（料号/名称/规格）；清空时回到层级树
  const filtered = useMemo(() => {
    const q = kw.trim().toLowerCase();
    if (!q) return null;
    return (rows.data ?? [])
      .filter((r) => [r.part_no, r.name, r.spec].some((v) => (v ?? '').toLowerCase().includes(q)))
      .map((r, i) => ({ ...r, key: `f${i}` }) as TreeRow);
  }, [kw, rows.data]);

  // 规格书上传的料号自动补全：BOM 内所有料号去重
  const partOptions = useMemo(
    () =>
      [...new Set((rows.data ?? []).map((r) => r.part_no).filter(Boolean))].map((v) => ({
        value: v,
      })),
    [rows.data],
  );

  const { tree, parentKeys } = useMemo(() => buildBomTree(rows.data ?? []), [rows.data]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  // 数据到位后默认全部展开（层级一次显示完）；用户仍可手动收起
  useEffect(() => {
    setExpandedKeys(parentKeys);
  }, [parentKeys]);
  const allExpanded = parentKeys.length > 0 && expandedKeys.length >= parentKeys.length;

  if (!tid) return <NoTenant />;
  if (boms.isLoading) return <Spin />;

  // 尚无 BOM：引导上传
  if (!m) {
    return (
      <Card>
        <Empty description="本产品还没有 BOM，上传一份 Excel 即可">
          {canManage && (
            <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
              上传 BOM
            </Button>
          )}
        </Empty>
        {uploadOpen && (
          <BomUploadModal
            tid={tid}
            open={uploadOpen}
            mode="create"
            onClose={() => setUploadOpen(false)}
          />
        )}
      </Card>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} align="start">
          <Descriptions title={m.name} column={2} size="small" style={{ maxWidth: 680 }}>
            <Descriptions.Item label="编码">{m.code ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="物料行数">{m.row_count ?? 0}</Descriptions.Item>
            <Descriptions.Item label="来源文件">{m.source_file ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{fmtTime(m.uploaded_at)}</Descriptions.Item>
          </Descriptions>
          <Space>
            {m.source_file_s3_key && (
              <Button
                icon={<DownloadOutlined />}
                onClick={() =>
                  openAttachment(tid, m.source_file_s3_key!).catch(() => message.error('下载失败'))
                }
              >
                下载原文件
              </Button>
            )}
            {canManage && (
              <>
                <Button
                  icon={<MailOutlined />}
                  loading={resend.isPending}
                  onClick={() => setSendOpen(true)}
                >
                  发送邮件
                </Button>
                <Button icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
                  重新上传（覆盖）
                </Button>
                <Popconfirm
                  title="删除整份 BOM？"
                  description="将连同全部物料行与历史快照一并删除，不可恢复；如需改内容请用「重新上传（覆盖）」"
                  okText="删除整份"
                  okButtonProps={{ danger: true }}
                  cancelText="取消"
                  onConfirm={() =>
                    del.mutate(m.id, {
                      onSuccess: () => message.success('已删除整份 BOM'),
                      onError: (e) =>
                        message.error(
                          `删除失败：${e instanceof ApiError ? e.message : (e as Error).message}`,
                        ),
                    })
                  }
                >
                  <Button danger icon={<DeleteOutlined />}>
                    删除整份 BOM
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        </Space>
      </Card>

      <Tabs
        items={[
          {
            key: 'bom',
            label: 'BOM 明细',
            children: (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder="搜索 料号 / 名称 / 规格"
                    style={{ width: 300 }}
                    value={kw}
                    onChange={(e) => setKw(e.target.value)}
                  />
                  <Space size="small">
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      disabled={!(rows.data && rows.data.length > 0)}
                      onClick={() =>
                        exportBomRowsToXlsx(rows.data ?? [], `${m.code || m.name}_BOM.xlsx`)
                      }
                    >
                      导出 Excel
                    </Button>
                    {!filtered && (
                      <Button
                        size="small"
                        onClick={() => setExpandedKeys(allExpanded ? [] : parentKeys)}
                      >
                        {allExpanded ? '全部收起' : '全部展开'}
                      </Button>
                    )}
                  </Space>
                </Space>

                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {filtered
                    ? `找到 ${filtered.length} 条匹配（扁平列表）`
                    : '按母件→子件层级展示（默认全部展开）；点击「料号」查看该物料的 ECN 变更时间线。'}
                </Typography.Text>

                <Table<TreeRow>
                  rowKey="key"
                  loading={rows.isLoading}
                  dataSource={filtered ?? tree}
                  size="small"
                  scroll={{ x: 1320, y: 560 }}
                  pagination={false}
                  {...(!filtered && {
                    expandable: {
                      expandedRowKeys: expandedKeys,
                      onExpandedRowsChange: (keys: readonly Key[]) =>
                        setExpandedKeys(keys as string[]),
                    },
                  })}
                  columns={[
                    {
                      title: '料号 / 层级结构',
                      dataIndex: 'part_no',
                      width: 240,
                      render: (v: string) => (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0 }}
                          onClick={() => setTimelinePart(v)}
                        >
                          {v}
                        </Button>
                      ),
                    },
                    { title: '名称', dataIndex: 'name', width: 160, ellipsis: true },
                    { title: '规格', dataIndex: 'spec', ellipsis: true },
                    { title: '用量', dataIndex: 'qty', width: 60 },
                    { title: '单位', dataIndex: 'unit', width: 56 },
                    {
                      title: '位号',
                      dataIndex: 'designator',
                      width: 150,
                      ellipsis: true,
                      render: (v?: string) =>
                        v ? (
                          <Tooltip title={v}>
                            <span>{v}</span>
                          </Tooltip>
                        ) : (
                          '—'
                        ),
                    },
                    {
                      title: '母件编号',
                      width: 120,
                      render: (_: unknown, r: TreeRow) => ex(r, '母件编号') || '—',
                    },
                    {
                      title: '归属BOM',
                      width: 90,
                      render: (_: unknown, r: TreeRow) => ex(r, '归属BOM') || '—',
                    },
                    {
                      title: '子BOM',
                      width: 90,
                      render: (_: unknown, r: TreeRow) => {
                        const s = ex(r, '子BOMID');
                        return s && s !== '0' ? s : '—';
                      },
                    },
                    {
                      title: '状态',
                      dataIndex: 'change_status',
                      width: 72,
                      render: (v?: string) =>
                        v ? <Tag color={v === '增加' ? 'green' : 'orange'}>{v}</Tag> : '—',
                    },
                    {
                      title: 'ECN编号',
                      dataIndex: 'ecn_no',
                      width: 160,
                      render: (v: string | undefined, r: TreeRow) => {
                        const hit = affected.data?.[r.part_no];
                        return (
                          <Space size={4} wrap>
                            {v ? <span>{v}</span> : null}
                            {hit && (
                              <Tag
                                color="orange"
                                style={{ cursor: 'pointer', marginInlineEnd: 0 }}
                                onClick={() => setTimelinePart(r.part_no)}
                              >
                                ECN {fmtYmd(hit.ecn_date)}
                              </Tag>
                            )}
                            {!v && !hit && '—'}
                          </Space>
                        );
                      },
                    },
                  ]}
                />
              </Space>
            ),
          },
          {
            key: 'ecn',
            label: 'ECN 变更',
            children: <EcnListPage />,
          },
          {
            key: 'spec',
            label: '规格书',
            children: <SpecListPage partOptions={partOptions} />,
          },
        ]}
      />

      {uploadOpen && (
        <BomUploadModal
          tid={tid}
          open={uploadOpen}
          mode="replace"
          bomId={m.id}
          onClose={() => setUploadOpen(false)}
        />
      )}
      <PartTimelineDrawer
        tid={tid}
        partNo={timelinePart}
        onClose={() => setTimelinePart(undefined)}
      />
      <SendMailModal
        open={sendOpen}
        title="发送 BOM 通知邮件"
        loading={resend.isPending}
        onCancel={() => setSendOpen(false)}
        onSend={(subject, body) =>
          resend.mutate(
            { bid: m.id, ...(subject ? { subject } : {}), ...(body ? { body } : {}) },
            {
              onSuccess: (r) => {
                if (r.notified) message.success(`已发送通知邮件（${r.recipients} 位收件人）`);
                else message.error(`发送失败：${r.error ?? '未知原因'}`);
                setSendOpen(false);
              },
              onError: (e) => message.error((e as Error).message || '发送失败'),
            },
          )
        }
      />
    </Space>
  );
}
